import { FiCheck, FiChevronRight, FiAlertCircle } from "react-icons/fi";
import { CategoryIcon } from "@/domains/categories/components/CategoryIcon";
import { IconPickerDialog } from "@/domains/categories/components/CategoryIconPicker";
import { showAppMessage } from "@/components/ui/confirmDialog";
import { useTagColors, useTagIcons } from "@/hooks/useTags";
import "@/domains/categories/categories.css";
import "../tags.css";
import { useState, useEffect } from 'react';
import { useTranslation } from "react-i18next";
import { ModalShell } from "@/components/ui/ModalShell";
import { FormField, ModalActions, TextInput, TextArea, AppButton } from "@/components/ui/primitives";

interface TagData {
  name: string;
  description?: string;
  color_id?: number | null;
  icon_id?: number | null;
}

interface Tag {
  id: number;
  name: string;
  description?: string;
  color_id?: number | null;
  icon_id?: number | null;
}

interface NewTagModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (tagData: TagData, tagId?: number) => void | Promise<void>;
  tag?: Tag | null; // Optional tag for edit mode
}

export const NewTagModal = ({ isOpen, onClose, onSubmit, tag }: NewTagModalProps) => {
  const { t } = useTranslation(["common", "tags"]);
  const [formData, setFormData] = useState<TagData>({
    name: '',
    description: '',
    icon_id: null,
    color_id: null
  });
  const [isIconPickerOpen, setIsIconPickerOpen] = useState(false);
  const [nameError, setNameError] = useState(false);
  const iconsQuery = useTagIcons({ enabled: isOpen });
  const colorsQuery = useTagColors({ enabled: isOpen });
  const [submitError, setSubmitError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditMode = !!tag;

  // Populate form when editing
  useEffect(() => {
    setSubmitError(false);
    setNameError(false);
    setIsIconPickerOpen(false);
    if (tag && isOpen) {
      setFormData({
        name: tag.name || '',
        description: tag.description ?? '',
        icon_id: tag.icon_id ?? null,
        color_id: tag.color_id ?? null
      });
    } else if (!isOpen) {
      // Reset form when modal closes
      setFormData({
        name: '',
        description: '',
        icon_id: null,
        color_id: null
      });
      setIsSubmitting(false);
    }
  }, [tag, isOpen]);

  const handleInputChange = (field: keyof TagData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) { setNameError(true); return; }
    setIsSubmitting(true);
    setSubmitError(false);
    try {
      await onSubmit({ ...formData, name: formData.name.trim() }, tag?.id);
      showAppMessage({ title: t(isEditMode ? "tags:changesSaved" : "tags:tagCreated", { name: formData.name.trim() }), tone: "success" });
      onClose();
    } catch {
      setSubmitError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const title = isEditMode
    ? `${t("common.actions.edit")} ${t("common.labels.tag").toLowerCase()}`
    : `${t("common.actions.create")} ${t("common.labels.tag").toLowerCase()}`;
  const submitLabel = isEditMode
    ? t("tags:saveChanges")
    : `${t("common.actions.create")} ${t("common.labels.tag").toLowerCase()}`;

  const selectedIcon = iconsQuery.data?.find((icon) => icon.id === formData.icon_id);
  const selectedColor = colorsQuery.data?.find((color) => color.id === formData.color_id);
  if (isOpen && isIconPickerOpen) return <IconPickerDialog iconsQuery={iconsQuery} value={formData.icon_id} onCancel={() => setIsIconPickerOpen(false)} onSelect={(icon_id) => { setFormData((data) => ({ ...data, icon_id })); setIsIconPickerOpen(false); }} />;

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      labelledBy="tag-modal-title"
      title={title}
      subtitle={t(isEditMode ? "tags:editSubtitle" : "tags:createSubtitle")}
      onSubmit={handleSubmit}
      noValidate
      mobileSheet
      variant="creation"
      maxWidthClassName="max-w-[560px]"
      panelClassName="category-modal-panel tag-modal-panel"
      disableClose={isSubmitting}
      footer={
        <ModalActions
          cancelLabel={t("common.actions.cancel")}
          submitLabel={submitLabel}
          submittingLabel={t("common.states.creating")}
          onCancel={onClose}
          isSubmitting={isSubmitting}
                  />
      }
    >
      <div className="app-creation-form category-creation-form mx-auto w-full max-w-xl">
        <h3 className="category-form-section">{t("tags.principalSection", { defaultValue: "Principal" })}</h3>
        <FormField label={t("tags:nameLabel")} htmlFor="tag-name" required error={nameError ? <><FiAlertCircle className="inline mr-1" />{t("tags:nameRequired")}</> : undefined} errorId="tag-name-error">
          <TextInput
            id="tag-name"
            type="text"
            value={formData.name}
            onChange={(e) => { handleInputChange('name', e.target.value); setNameError(false); }}
            aria-invalid={nameError}
            aria-describedby={nameError ? 'tag-name-error' : undefined}
            placeholder={t("tags:namePlaceholder")}
            required
            disabled={isSubmitting}
            autoFocus
          />
        </FormField>

        <h3 className="category-form-section">{t("tags.customizationSection", { defaultValue: "Personalización" })}</h3>
        <div className="category-preview" aria-live="polite">
          <span className="category-preview-dot" style={{ backgroundColor: selectedColor?.hex, color: selectedColor ? "var(--ds-on-card)" : undefined }}><CategoryIcon name={selectedIcon?.name} size={28} /></span>
          <div><b>{formData.name || t("tags:previewName")}</b><small>{t("tags:previewCaption")}</small></div>
        </div>
        <fieldset disabled={isSubmitting} className="category-color-field">
          <legend className="category-color-label">{t("accounts:accounts.colorLabel", { defaultValue: "Color" })} <span>{t("common.optional", { defaultValue: "Opcional" })}</span></legend>
          {colorsQuery.isLoading ? <p role="status">{t("tags.colorsLoading", { defaultValue: "Cargando colores…" })}</p> : colorsQuery.isError ? <div role="status"><p>{t("tags.colorsLoadError", { defaultValue: "No se pudieron cargar los colores." })}</p><button type="button" className="app-focus-ring underline" onClick={() => colorsQuery.refetch()}>{t("common.actions.retry", { defaultValue: "Reintentar" })}</button></div> : (
            <div className="category-color-options">
              {colorsQuery.data?.map((color) => <label key={color.id} className="category-color-option"><input type="radio" className="peer sr-only" name="tag-color" value={color.id} checked={formData.color_id === color.id} onClick={(event) => { if (formData.color_id === color.id) { event.preventDefault(); setFormData((data) => ({ ...data, color_id: null })); } }} onChange={() => setFormData((data) => ({ ...data, color_id: color.id }))} aria-label={t(`accounts:accounts.colorNames.${color.name}`, { defaultValue: color.name })} /><span className="category-color-swatch" style={{ backgroundColor: color.hex }}>{formData.color_id === color.id && <FiCheck />}</span></label>)}
            </div>
          )}
        </fieldset>
        <FormField label={<><span>{t("tags:iconLabel")}</span><span className="app-form-required">{t("common:labels.optional")}</span></>} htmlFor="tag-icon-select">
          <AppButton id="tag-icon-select" tone="ghost" disabled={isSubmitting} onClick={() => setIsIconPickerOpen(true)} className="category-icon-select">
            <span className="category-icon-select-dot" style={{ backgroundColor: selectedColor?.hex, color: selectedColor ? "var(--ds-on-card)" : undefined }}><CategoryIcon name={selectedIcon?.name} size={18} /></span>
            <span>{selectedIcon?.label ?? t("categories:noIcon")}</span><FiChevronRight aria-hidden="true" />
          </AppButton>
        </FormField>
        <h3 className="category-form-section">{t("common:labels.optional")}</h3>
        <FormField label={t("common:labels.description")} htmlFor="tag-description" optionalLabel={t("common:labels.optional")}>
          <TextArea id="tag-description" value={formData.description ?? ""} onChange={(event) => handleInputChange("description", event.target.value)} disabled={isSubmitting} rows={2} />
        </FormField>
        {submitError && <p role="alert" className="text-sm text-[var(--ds-negative)]">{t("tags.saveError", { defaultValue: "No se pudo guardar la categoría. Revisa los datos e intenta de nuevo." })}</p>}
      </div>
    </ModalShell>
  );
};
